import { safeLiquidRender } from '@shared/services/liquidUtils'
import { stripHtml } from '@shared/services/text'

export interface PhraseSource {
  id: string
  fallbackText: string
  map?: Record<string, string>
}

/** A run of display text; `phrase` marks words a phrase token produced. */
export interface PhraseSegment {
  text: string
  phrase: boolean
}

const PHRASE_TOKEN = /\{\{\s*phrases\.(\w+)\s*\}\}/g
const UNRESOLVED_PHRASE = 'missing phrase'

function phraseValue(phrase: PhraseSource): string {
  const fallback = stripHtml(safeLiquidRender(phrase.fallbackText || '', {})).trim()
  if (fallback) {
    return fallback
  }
  const firstAnswerText = Object.values(phrase.map ?? {}).find((value) => stripHtml(value).trim())
  return firstAnswerText ? stripHtml(firstAnswerText).trim() : UNRESOLVED_PHRASE
}

/**
 * Renders config text the way the dashboard previews do (phrase fallback text,
 * then Liquid) as plain text, keeping track of which words came from a phrase.
 * Earlier phrase lists win, so pass the draft's phrases before the published ones.
 */
export function phraseSegments(text: string, ...phraseLists: readonly (readonly PhraseSource[])[]): PhraseSegment[] {
  const plain = stripHtml(text)
  if (!plain.includes('{{') && !plain.includes('{%')) {
    return plain ? [{ text: plain, phrase: false }] : []
  }

  const values: Record<string, string> = {}
  for (const phrases of [...phraseLists].reverse()) {
    for (const phrase of phrases) {
      values[phrase.id] = phraseValue(phrase)
    }
  }
  const context = { phrases: values }

  // Tags and filtered references only make sense rendered as one template.
  if (plain.includes('{%') || /\{\{\s*phrases\.\w+\s*\|/.test(plain)) {
    const rendered = safeLiquidRender(plain, context)
    return rendered ? [{ text: rendered, phrase: false }] : []
  }

  const segments: PhraseSegment[] = []
  plain.split(PHRASE_TOKEN).forEach((part, index) => {
    const segment =
      index % 2 === 1
        ? { text: values[part] ?? UNRESOLVED_PHRASE, phrase: true }
        : { text: safeLiquidRender(part, context), phrase: false }
    if (segment.text) {
      segments.push(segment)
    }
  })
  return segments
}

/** `phraseSegments` joined into one plain string. */
export function phrasePlainText(text: string, ...phraseLists: readonly (readonly PhraseSource[])[]): string {
  return phraseSegments(text, ...phraseLists)
    .map((segment) => segment.text)
    .join('')
}
